import React, { useEffect, useMemo, useState } from 'react';
import InlineEditableText from '../InlineEditableText';
import ReportExchangeBlock from './ReportExchangeBlock';
import { buildReportCaseLlmJson } from './buildReportCaseLlmJson';
import { RERUN_MESSAGE_SOURCE } from './rerunMessageSource';
import { isReportCaseReviewed, summarizeReportCaseReviews } from './reportCaseReview';
import { filterReportCasesBySearch } from './filterReportCasesBySearch';

const STATUS_PILL_CLASSES = {
  completed: 'bg-emerald-100 text-emerald-700',
  completed_with_failures: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-200 text-slate-700',
  running: 'bg-blue-100 text-blue-700',
  skipped: 'bg-gray-200 text-gray-600',
};

const STATUS_LABEL = {
  completed: 'Completed',
  completed_with_failures: 'Completed with failures',
  failed: 'Failed',
  cancelled: 'Cancelled',
  running: 'Running',
  skipped: 'Skipped',
};

const formatStatusLabel = (status) => STATUS_LABEL[status] || (status ? status.replace(/_/g, ' ') : 'Unknown');

const StatusPill = ({ status, className = '' }) => {
  const cls = STATUS_PILL_CLASSES[status] || 'bg-gray-200 text-gray-700';
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-full ${cls} ${className}`}>
      {formatStatusLabel(status)}
    </span>
  );
};

const formatTimestamp = (value) => {
  if (!value) return '—';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch (e) {
    return '—';
  }
};

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return '—';
  try {
    const startMs = new Date(startedAt).getTime();
    const endMs = new Date(finishedAt).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return '—';
    const totalSec = Math.round((endMs - startMs) / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m === 0) return `${s}s`;
    return `${m}m${s.toString().padStart(2, '0')}s`;
  } catch (e) {
    return '—';
  }
};

const PRINT_STATUS_COLORS = {
  completed: { bg: '#D1FAE5', fg: '#047857' },
  completed_with_failures: { bg: '#FEF3C7', fg: '#92400E' },
  failed: { bg: '#FEE2E2', fg: '#B91C1C' },
  cancelled: { bg: '#E2E8F0', fg: '#334155' },
  running: { bg: '#DBEAFE', fg: '#1D4ED8' },
  skipped: { bg: '#E5E7EB', fg: '#4B5563' },
};

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const renderPrintStatusPill = (status) => {
  const palette = PRINT_STATUS_COLORS[status] || { bg: '#E5E7EB', fg: '#374151' };
  return `<span class="status-pill" style="background:${palette.bg};color:${palette.fg};">${escapeHtml(
    formatStatusLabel(status)
  )}</span>`;
};

const buildPrintableReportHtml = (report, persistedMeta, clinicName, stats) => {
  const planName = report?.plan_name || persistedMeta?.plan_name || 'Run report';
  const status = report?.status || 'running';
  const startedAt = formatTimestamp(report?.started_at);
  const finishedAt = formatTimestamp(report?.finished_at);
  const duration = formatDuration(report?.started_at, report?.finished_at);
  const notes = (report?.notes || '').trim();
  const reportId = persistedMeta?.id || '';
  const cases = Array.isArray(report?.cases) ? report.cases : [];

  const casesHtml = cases
    .map((caseItem, idx) => {
      const caseStatus = caseItem?.status || 'completed';
      const exchanges = Array.isArray(caseItem?.exchanges) ? caseItem.exchanges : [];
      const exchangesHtml = exchanges
        .map((exchange) => {
          const userText = escapeHtml(exchange?.message || '(empty)');
          const assistantText = escapeHtml(exchange?.answer || '[No reply]');
          return `
            <div class="bubble-row align-right">
              <div class="bubble bubble-user">${userText}</div>
            </div>
            <div class="bubble-row align-left">
              <div class="bubble bubble-assistant">${assistantText}</div>
            </div>
          `;
        })
        .join('');
      const failedTail =
        caseStatus === 'failed'
          ? `<div class="bubble-row align-left"><div class="bubble bubble-noreply">[No reply]</div></div>`
          : '';
      const errorBanner = caseItem?.error
        ? `<div class="case-error">${escapeHtml(caseItem.error)}</div>`
        : '';
      const noteBanner = caseItem?.note
        ? `<div class="case-note">${escapeHtml(caseItem.note)}</div>`
        : '';
      const sessionLabel = caseItem?.session_id ? escapeHtml(caseItem.session_id) : '';
      const body =
        exchanges.length === 0
          ? `<div class="case-empty">${
              caseStatus === 'skipped' ? 'Case was skipped.' : 'No exchanges recorded for this case.'
            }</div>`
          : `<div class="bubbles">${exchangesHtml}${failedTail}</div>`;

      return `
        <section class="case-card">
          <header class="case-header">
            <div class="case-title">
              <span class="case-index">${idx + 1}.</span>
              <span class="case-name">${escapeHtml(caseItem?.name || `Case ${idx + 1}`)}</span>
              ${renderPrintStatusPill(caseStatus)}
            </div>
            ${sessionLabel ? `<div class="case-session">${sessionLabel}</div>` : ''}
          </header>
          ${errorBanner}
          ${noteBanner}
          ${body}
        </section>
      `;
    })
    .join('');

  const notesBlock = notes
    ? `<section class="notes"><h2>Notes</h2><div class="notes-body">${escapeHtml(notes).replace(/\n/g, '<br/>')}</div></section>`
    : '';

  const clinicLine = clinicName ? `<div class="meta-row">Clinic: ${escapeHtml(clinicName)}</div>` : '';
  const reportIdLine = reportId ? `<div class="meta-row meta-muted">Report ID: ${escapeHtml(reportId)}</div>` : '';

  return `
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(planName)} — Run report</title>
    <style>
      @page { size: A4; margin: 16mm 14mm; }
      * { box-sizing: border-box; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "PingFang SC", "Microsoft YaHei";
        color: #111827;
        font-size: 12px;
        line-height: 1.45;
        margin: 0;
        padding: 0;
        background: #FFFFFF;
      }
      h1, h2 { margin: 0; }
      h1 { font-size: 18px; }
      h2 { font-size: 13px; margin-bottom: 6px; }
      header.report-header { border-bottom: 1px solid #E5E7EB; padding-bottom: 10px; margin-bottom: 14px; }
      .title-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .meta-row { color: #4B5563; font-size: 11px; margin-top: 4px; }
      .meta-muted { color: #9CA3AF; }
      .status-pill { display: inline-block; padding: 1px 8px; border-radius: 9999px; font-size: 10px; font-weight: 600; }
      .stats-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; font-size: 11px; }
      .stats-row .chip { padding: 2px 8px; border-radius: 4px; background: #F3F4F6; border: 1px solid #E5E7EB; color: #374151; }
      .stats-row .chip-completed { background: #ECFDF5; border-color: #A7F3D0; color: #047857; }
      .stats-row .chip-failed { background: #FEF2F2; border-color: #FECACA; color: #B91C1C; }
      .stats-row .chip-skipped { background: #F9FAFB; border-color: #E5E7EB; color: #6B7280; }
      .case-card {
        border: 1px solid #E5E7EB;
        border-radius: 8px;
        margin-bottom: 12px;
        background: #FFFFFF;
        overflow: hidden;
        page-break-inside: avoid;
      }
      .case-header {
        background: #F9FAFB;
        border-bottom: 1px solid #F3F4F6;
        padding: 6px 10px;
      }
      .case-title { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
      .case-index { color: #9CA3AF; font-size: 11px; min-width: 22px; }
      .case-name { font-weight: 600; color: #111827; font-size: 12px; }
      .case-session { color: #9CA3AF; font-size: 10px; margin-top: 2px; }
      .case-error {
        background: #FEF2F2;
        color: #B91C1C;
        border-bottom: 1px solid #FECACA;
        padding: 6px 10px;
        font-size: 11px;
      }
      .case-note {
        background: #EFF6FF;
        color: #1D4ED8;
        border-bottom: 1px solid #BFDBFE;
        padding: 6px 10px;
        font-size: 11px;
      }
      .case-empty { padding: 8px 10px; color: #6B7280; font-size: 11px; }
      .bubbles { padding: 10px; background: #F7F8FA; display: flex; flex-direction: column; gap: 6px; }
      .bubble-row { display: flex; }
      .bubble-row.align-right { justify-content: flex-end; }
      .bubble-row.align-left { justify-content: flex-start; }
      .bubble {
        max-width: 80%;
        padding: 6px 10px;
        border-radius: 10px;
        font-size: 11px;
        white-space: pre-wrap;
        word-wrap: break-word;
      }
      .bubble-user { background: #D9FDD3; color: #111827; border-bottom-right-radius: 3px; }
      .bubble-assistant { background: #FFFFFF; color: #111827; border: 1px solid #F3F4F6; border-bottom-left-radius: 3px; }
      .bubble-noreply { background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA; }
      .notes { margin-top: 18px; padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 8px; background: #F9FAFB; }
      .notes-body { color: #1F2937; font-size: 11px; white-space: pre-wrap; }
      footer.print-footer { margin-top: 18px; color: #9CA3AF; font-size: 10px; text-align: right; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    <header class="report-header">
      <div class="title-row">
        <h1>${escapeHtml(planName)}</h1>
        ${renderPrintStatusPill(status)}
      </div>
      ${clinicLine}
      <div class="meta-row">Started: ${escapeHtml(startedAt)} &nbsp;|&nbsp; Finished: ${escapeHtml(
    finishedAt
  )} &nbsp;|&nbsp; Duration: ${escapeHtml(duration)}</div>
      <div class="stats-row">
        <span class="chip">Total ${stats.total}</span>
        <span class="chip chip-completed">Completed ${stats.completed}</span>
        <span class="chip chip-failed">Failed ${stats.failed}</span>
        <span class="chip chip-skipped">Skipped ${stats.skipped}</span>
      </div>
      ${reportIdLine}
    </header>
    ${casesHtml || '<div class="case-empty">No cases to display.</div>'}
    ${notesBlock}
    <footer class="print-footer">Generated ${escapeHtml(new Date().toLocaleString())}</footer>
  </body>
</html>
  `;
};

const printReportViaIframe = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  const cleanup = () => {
    window.setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 500);
  };

  iframe.onload = () => {
    try {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        return;
      }
      win.focus();
      // Some browsers fire onload before the document is fully ready for print.
      window.setTimeout(() => {
        try {
          win.print();
        } catch (e) {
          // ignored — caller surfaces a generic failure via setActionError
        }
        cleanup();
      }, 100);
    } catch (e) {
      cleanup();
    }
  };

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    throw new Error('Unable to access print iframe document.');
  }
  doc.open();
  doc.write(html);
  doc.close();
};

const computeStats = (report) => {
  const cases = Array.isArray(report?.cases) ? report.cases : [];
  let completed = 0;
  let failed = 0;
  let skipped = 0;
  cases.forEach((c) => {
    const status = c?.status || '';
    if (status === 'completed') completed += 1;
    else if (status === 'failed') failed += 1;
    else if (status === 'skipped') skipped += 1;
  });
  return {
    total: cases.length,
    completed,
    failed,
    skipped,
  };
};

/**
 * Redesigned, human-readable run report modal.
 *
 * Light theme, status header, filter tabs, chat-style exchanges, notes textarea,
 * past reports dropdown, save/export/delete actions.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object|null} props.report - The currently displayed run report (in-memory or loaded).
 * @param {Object|null} props.persistedMeta - If the displayed report is persisted in the DB, { id, plan_name, ... }.
 * @param {Array<Object>} props.savedReports - List of past saved reports (summaries) for the picker.
 * @param {boolean} props.loadingSavedReports
 * @param {Function} props.onSelectSavedReport - (reportId: string) => Promise<void>; loads the report into the modal.
 * @param {Function} props.onSaveCurrentReport - ({ notes, name }?) => Promise<{success, id?}>
 * @param {Function} props.onDeleteSavedReport - (reportId: string) => Promise<{success}>
 * @param {Function} props.onUpdateNotes      - (reportId: string, notes: string) => Promise<{success}>
 * @param {Function} props.onUpdateName       - (reportId: string|null, name: string) => Promise<{success}>
 * @param {Function} props.onOpenConversation - (sessionId: string) => void
 * @param {Function} props.onMutateExchange - (caseItem, exchangeIndex, mutation) => Promise<{success, error?}>; edit or delete an exchange (`mutation.action` is `update` or `delete`).
 * @param {Function} props.onSetCaseReviewed - (caseItem, reviewed: boolean) => Promise<{success, error?}>; mark or unmark a case as reviewed.
 * @param {Function} props.onDeleteCase - (caseItem) => Promise<{success, error?}>; delete a case from the current report.
 * @param {Function} props.onEditCaseInPlan - (caseItem) => Promise<{success, error?}>; open the linked test plan editor focused on this case.
 * @param {Function} props.onRerunCases - (caseItem[] | caseItem, options?) => Promise<{success, error?, message?}>; re-runs one or more cases (in parallel) and updates the report. `options.messageSource` is `report` (default) or `plan` (latest test-plan messages).
 * @param {Array<number>} props.rerunningCaseIndices - case_index values currently being re-run (drives per-case spinner / disabling).
 * @param {string} props.clinicName
 */
const ApiTestRunReportModal = ({
  isOpen,
  onClose,
  report,
  persistedMeta,
  savedReports,
  loadingSavedReports,
  onSelectSavedReport,
  onSaveCurrentReport,
  onDeleteSavedReport,
  onUpdateNotes,
  onUpdateName,
  onOpenConversation,
  onMutateExchange,
  onSetCaseReviewed,
  onDeleteCase,
  onEditCaseInPlan,
  onRerunCases,
  rerunningCaseIndices = [],
  clinicName,
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [caseSearchQuery, setCaseSearchQuery] = useState('');
  const [selectedCaseKeys, setSelectedCaseKeys] = useState(() => new Set());
  const [notesDraft, setNotesDraft] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [deletingReport, setDeletingReport] = useState(false);
  const [savingExchangeKey, setSavingExchangeKey] = useState(null);
  const [copiedCaseKey, setCopiedCaseKey] = useState(null);
  const [savingReviewCaseKey, setSavingReviewCaseKey] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const persistedId = persistedMeta?.id || null;
  const reportNotes = report?.notes || '';
  const reportName = report?.plan_name || persistedMeta?.plan_name || 'Run report';

  useEffect(() => {
    setActiveFilter('all');
    setCaseSearchQuery('');
    setSelectedCaseKeys(new Set());
    setActionError(null);
    setActionMessage(null);
  }, [report?.id, report?.started_at]);

  useEffect(() => {
    setNotesDraft(reportNotes || '');
  }, [reportNotes, persistedId]);

  useEffect(() => {
    setNameDraft(reportName || '');
  }, [reportName, persistedId]);

  const stats = useMemo(() => {
    if (
      report &&
      typeof report === 'object' &&
      (typeof report.case_total === 'number' || typeof report.case_completed === 'number')
    ) {
      return {
        total: report.case_total ?? 0,
        completed: report.case_completed ?? 0,
        failed: report.case_failed ?? 0,
        skipped: report.case_skipped ?? 0,
      };
    }
    return computeStats(report);
  }, [report]);

  const cases = Array.isArray(report?.cases) ? report.cases : [];
  const reviewStats = useMemo(() => summarizeReportCaseReviews(cases), [cases]);
  const statusFilteredCases = useMemo(() => {
    if (activeFilter === 'all') return cases;
    if (activeFilter === 'reviewed') return cases.filter((c) => isReportCaseReviewed(c));
    if (activeFilter === 'needs_review') return cases.filter((c) => !isReportCaseReviewed(c));
    return cases.filter((c) => c?.status === activeFilter);
  }, [cases, activeFilter]);
  const filteredCases = useMemo(
    () => filterReportCasesBySearch(statusFilteredCases, caseSearchQuery),
    [statusFilteredCases, caseSearchQuery]
  );
  const hasCaseSearch = (caseSearchQuery || '').trim().length > 0;

  const handleNameBlur = async () => {
    if (typeof onUpdateName !== 'function') return;
    const trimmed = (nameDraft || '').trim() || 'Plan';
    if (trimmed === (reportName || '').trim()) return;
    setSavingName(true);
    setActionError(null);
    try {
      const result = await onUpdateName(persistedId, trimmed);
      if (!result?.success) {
        setActionError(result?.error || 'Failed to save report name.');
      } else {
        setActionMessage('Report name saved.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to save report name.');
    } finally {
      setSavingName(false);
    }
  };

  const handleNotesBlur = async () => {
    if (!persistedId || typeof onUpdateNotes !== 'function') return;
    if ((notesDraft || '') === (reportNotes || '')) return;
    setSavingNotes(true);
    setActionError(null);
    try {
      const result = await onUpdateNotes(persistedId, notesDraft || '');
      if (!result?.success) {
        setActionError(result?.error || 'Failed to save notes.');
      } else {
        setActionMessage('Notes saved.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveReport = async () => {
    if (typeof onSaveCurrentReport !== 'function') return;
    setSavingReport(true);
    setActionError(null);
    setActionMessage(null);
    try {
      const result = await onSaveCurrentReport({
        notes: notesDraft || '',
        name: (nameDraft || '').trim() || 'Plan',
      });
      if (!result?.success) {
        setActionError(result?.error || 'Failed to save report.');
      } else {
        setActionMessage('Report saved.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to save report.');
    } finally {
      setSavingReport(false);
    }
  };

  const handleExportJson = () => {
    if (!report) return;
    try {
      const json = JSON.stringify(report, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = `run-report-${report.plan_name || 'plan'}-${(report.started_at || new Date().toISOString()).replace(/[:.]/g, '-')}.json`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err?.message || 'Failed to export JSON.');
    }
  };

  const handleExportHtml = () => {
    if (!report) return;
    setActionError(null);
    try {
      // Use the in-memory notes draft so freshly-typed notes are included without
      // requiring the user to click "Save report" first (mirrors PDF export).
      const reportWithLatestNotes = { ...report, notes: notesDraft || report.notes || '' };
      const html = buildPrintableReportHtml(
        reportWithLatestNotes,
        persistedMeta,
        clinicName,
        stats
      );
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = `run-report-${report.plan_name || 'plan'}-${(report.started_at || new Date().toISOString()).replace(/[:.]/g, '-')}.html`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setActionMessage('HTML report exported.');
    } catch (err) {
      setActionError(err?.message || 'Failed to export HTML.');
    }
  };

  const handleExportPdf = () => {
    if (!report) return;
    setActionError(null);
    try {
      // Use the in-memory notes draft so the user does not need to click "Save report"
      // before exporting freshly-typed notes into the PDF.
      const reportWithLatestNotes = { ...report, notes: notesDraft || report.notes || '' };
      const html = buildPrintableReportHtml(
        reportWithLatestNotes,
        persistedMeta,
        clinicName,
        stats
      );
      printReportViaIframe(html);
      setActionMessage('Print dialog opened — choose "Save as PDF" to export.');
    } catch (err) {
      setActionError(err?.message || 'Failed to export PDF.');
    }
  };

  const handleDelete = async () => {
    if (!persistedId || typeof onDeleteSavedReport !== 'function') return;
    if (!window.confirm('Delete this saved report? This cannot be undone.')) return;
    setDeletingReport(true);
    setActionError(null);
    try {
      const result = await onDeleteSavedReport(persistedId);
      if (!result?.success) {
        setActionError(result?.error || 'Failed to delete report.');
      } else {
        setActionMessage('Report deleted.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to delete report.');
    } finally {
      setDeletingReport(false);
    }
  };

  // Stable per-case key: prefer case_index, fall back to name (older reports).
  const caseKey = (c) => (Number.isInteger(c?.case_index) ? `i:${c.case_index}` : `n:${c?.name || ''}`);
  const exchangeKey = (caseItem, exIdx) => `${caseKey(caseItem)}:e:${exIdx}`;
  const runningCaseKeys = useMemo(
    () => new Set((rerunningCaseIndices || []).map((i) => `i:${i}`)),
    [rerunningCaseIndices]
  );
  const isCaseRerunning = (c) => runningCaseKeys.has(caseKey(c));
  const anyRerunning = (rerunningCaseIndices || []).length > 0;
  const selectedCases = useMemo(
    () => cases.filter((c) => selectedCaseKeys.has(caseKey(c))),
    [cases, selectedCaseKeys]
  );

  const runRerun = async (items, options = {}) => {
    if (typeof onRerunCases !== 'function') return;
    const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
    if (list.length === 0) return;
    setActionError(null);
    setActionMessage(null);
    try {
      const result = await onRerunCases(list, options);
      if (result?.success === false) {
        setActionError(result?.error || 'Failed to re-run.');
      } else if (result?.message) {
        setActionMessage(result.message);
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to re-run.');
    }
  };

  const toggleCaseSelected = (caseItem) => {
    const key = caseKey(caseItem);
    setSelectedCaseKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectCaseKeys = (items) => setSelectedCaseKeys(new Set(items.map(caseKey)));

  const handleCopyCaseForLlm = async (caseItem) => {
    const key = caseKey(caseItem);
    const json = buildReportCaseLlmJson(caseItem);
    setActionError(null);
    try {
      await navigator.clipboard.writeText(json);
      setCopiedCaseKey(key);
      setActionMessage('Copied conversation JSON for LLM.');
      window.setTimeout(() => {
        setCopiedCaseKey((current) => (current === key ? null : current));
      }, 2000);
    } catch (err) {
      setActionError(err?.message || 'Failed to copy to clipboard.');
    }
  };

  const handleToggleCaseReviewed = async (caseItem) => {
    if (typeof onSetCaseReviewed !== 'function') return;
    const key = caseKey(caseItem);
    const nextReviewed = !isReportCaseReviewed(caseItem);
    setSavingReviewCaseKey(key);
    setActionError(null);
    try {
      const result = await onSetCaseReviewed(caseItem, nextReviewed);
      if (result?.success === false) {
        setActionError(result?.error || 'Failed to update review status.');
      } else {
        setActionMessage(nextReviewed ? 'Case marked as reviewed.' : 'Review mark removed.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to update review status.');
    } finally {
      setSavingReviewCaseKey(null);
    }
  };

  const handleEditCaseInPlan = async (caseItem) => {
    if (typeof onEditCaseInPlan !== 'function') return;
    setActionError(null);
    try {
      const result = await onEditCaseInPlan(caseItem);
      if (result?.success === false) {
        setActionError(result?.error || 'Failed to open test plan editor.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to open test plan editor.');
    }
  };

  const handleDeleteCase = async (caseItem) => {
    if (typeof onDeleteCase !== 'function') return;
    if (!window.confirm('Delete this test case from the report? This cannot be undone.')) return;
    setActionError(null);
    try {
      const result = await onDeleteCase(caseItem);
      if (result?.success === false) {
        setActionError(result?.error || 'Failed to delete test case.');
      } else {
        setActionMessage('Test case deleted from report.');
      }
    } catch (err) {
      setActionError(err?.message || 'Failed to delete test case.');
    }
  };

  const mutateExchange = async (caseItem, exchangeIndex, mutation) => {
    if (typeof onMutateExchange !== 'function') {
      return { success: false, error: 'Exchange editing is not available.' };
    }
    const key = exchangeKey(caseItem, exchangeIndex);
    setSavingExchangeKey(key);
    setActionError(null);
    try {
      const result = await onMutateExchange(caseItem, exchangeIndex, mutation);
      if (result?.success === false) {
        setActionError(result?.error || 'Failed to update exchange.');
      } else if (mutation.action === 'delete') {
        setActionMessage('Exchange deleted.');
      } else {
        setActionMessage('Exchange saved.');
      }
      return result;
    } catch (err) {
      const message = err?.message || 'Failed to update exchange.';
      setActionError(message);
      return { success: false, error: message };
    } finally {
      setSavingExchangeKey(null);
    }
  };

  if (!isOpen) return null;

  const startedAt = report?.started_at;
  const finishedAt = report?.finished_at;
  const status = report?.status || 'running';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[88vh] flex flex-col rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <InlineEditableText
                value={nameDraft}
                onChange={setNameDraft}
                onBlur={handleNameBlur}
                placeholder="Run report"
                disabled={!report}
                saving={savingName}
                title="Edit report name"
              />
              {savingName && <span className="text-[10px] text-gray-400">(saving…)</span>}
              <StatusPill status={status} />
              {persistedId && (
                <span className="text-[11px] text-gray-500">Saved</span>
              )}
            </div>
            {clinicName && (
              <div className="text-xs text-gray-500 truncate">Clinic: {clinicName}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={persistedId || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) return;
                if (typeof onSelectSavedReport === 'function') {
                  onSelectSavedReport(value);
                }
              }}
              disabled={loadingSavedReports}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none disabled:opacity-50"
              title="Open a past saved report"
            >
              <option value="">
                {loadingSavedReports
                  ? 'Loading past reports…'
                  : `Past reports (${(savedReports || []).length})`}
              </option>
              {(savedReports || []).map((row) => {
                const label = `${row.plan_name || 'Plan'} • ${formatTimestamp(row.started_at)} • ${formatStatusLabel(row.status)}`;
                return (
                  <option key={row.id} value={row.id}>
                    {label}
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-gray-200 bg-white flex flex-col gap-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
            <div>
              <span className="text-gray-400">Started:</span>{' '}
              <span className="text-gray-800">{formatTimestamp(startedAt)}</span>
            </div>
            <div>
              <span className="text-gray-400">Finished:</span>{' '}
              <span className="text-gray-800">{formatTimestamp(finishedAt)}</span>
            </div>
            <div>
              <span className="text-gray-400">Duration:</span>{' '}
              <span className="text-gray-800">{formatDuration(startedAt, finishedAt)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200">
              Total {stats.total}
            </span>
            <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
              Completed {stats.completed}
            </span>
            <span className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-100">
              Failed {stats.failed}
            </span>
            <span className="px-2 py-1 rounded bg-gray-50 text-gray-600 border border-gray-200">
              Skipped {stats.skipped}
            </span>
            <span className="px-2 py-1 rounded bg-violet-50 text-violet-700 border border-violet-100">
              Reviewed {reviewStats.reviewed}
            </span>
            <span className="px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-100">
              Needs review {reviewStats.needsReview}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1 max-w-md">
              <input
                type="search"
                value={caseSearchQuery}
                onChange={(e) => setCaseSearchQuery(e.target.value)}
                placeholder="Search cases (name, messages, error, note…)"
                className="w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-xs text-gray-800 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none"
                aria-label="Search test cases in this report"
              />
              {hasCaseSearch && (
                <button
                  type="button"
                  onClick={() => setCaseSearchQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm leading-none px-1"
                  aria-label="Clear case search"
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {hasCaseSearch && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {filteredCases.length} of {statusFilteredCases.length} shown
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'all', label: `All (${stats.total})` },
              { key: 'needs_review', label: `Needs review (${reviewStats.needsReview})` },
              { key: 'reviewed', label: `Reviewed (${reviewStats.reviewed})` },
              { key: 'failed', label: `Failed (${stats.failed})` },
              { key: 'completed', label: `Completed (${stats.completed})` },
              { key: 'skipped', label: `Skipped (${stats.skipped})` },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveFilter(tab.key)}
                className={`px-2.5 py-1 text-xs rounded-full border transition ${
                  activeFilter === tab.key
                    ? 'bg-[#00A884] text-white border-[#00A884]'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {typeof onRerunCases === 'function' && cases.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500 font-medium">Parallel re-run:</span>
              <button
                type="button"
                onClick={() => selectCaseKeys(cases.filter((c) => c?.status === 'failed'))}
                className="px-2 py-1 rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              >
                Select failed ({stats.failed})
              </button>
              <button
                type="button"
                onClick={() => selectCaseKeys(filteredCases)}
                className="px-2 py-1 rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              >
                Select all shown
              </button>
              <button
                type="button"
                onClick={() => setSelectedCaseKeys(new Set())}
                disabled={selectedCases.length === 0}
                className="px-2 py-1 rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => runRerun(selectedCases, { messageSource: RERUN_MESSAGE_SOURCE.REPORT })}
                disabled={selectedCases.length === 0}
                className="px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Re-run the selected cases using the user messages shown in this report"
              >
                Re-run selected ({selectedCases.length})
              </button>
              <button
                type="button"
                onClick={() => runRerun(selectedCases, { messageSource: RERUN_MESSAGE_SOURCE.PLAN })}
                disabled={selectedCases.length === 0}
                className="px-2.5 py-1 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Re-run the selected cases using the latest messages from the test plan"
              >
                Re-run selected (plan)
              </button>
              {anyRerunning && (
                <span className="inline-flex items-center gap-1 text-blue-600">
                  <svg className="animate-spin h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Re-running {rerunningCaseIndices.length}…
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 bg-[#F0F2F5]">
          {!report ? (
            <div className="text-sm text-gray-500 py-10 text-center">
              No run report available yet.
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">
              {hasCaseSearch
                ? 'No cases match your search.'
                : 'No cases match this filter.'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCases.map((caseItem, idx) => {
                const caseStatus = caseItem?.status || 'completed';
                const exchanges = Array.isArray(caseItem?.exchanges) ? caseItem.exchanges : [];
                const isThisCaseRerunning = isCaseRerunning(caseItem);
                const isThisCaseSelected = selectedCaseKeys.has(caseKey(caseItem));
                const isReviewed = isReportCaseReviewed(caseItem);
                return (
                  <div
                    key={`${caseItem?.session_id || 'no-session'}-${idx}`}
                    className={`rounded-lg border bg-white shadow-sm overflow-hidden ${
                      isReviewed ? 'border-emerald-200 border-l-4 border-l-emerald-500' : 'border-gray-200'
                    }`}
                  >
                    <div className={`px-3 py-2 flex items-center justify-between gap-2 border-b ${
                      isReviewed ? 'border-emerald-100 bg-emerald-50/60' : 'border-gray-100 bg-gray-50'
                    }`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {typeof onRerunCases === 'function' && (
                          <input
                            type="checkbox"
                            checked={isThisCaseSelected}
                            onChange={() => toggleCaseSelected(caseItem)}
                            className="accent-blue-600 flex-shrink-0"
                            title="Select this case for parallel re-run"
                            aria-label={`Select ${caseItem?.name || `Case ${idx + 1}`} for re-run`}
                          />
                        )}
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {caseItem?.name || `Case ${idx + 1}`}
                        </span>
                        <StatusPill status={caseStatus} />
                        {isReviewed && (
                          <span className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 text-emerald-700">
                            Reviewed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {typeof onSetCaseReviewed === 'function' && (
                          <button
                            type="button"
                            onClick={() => handleToggleCaseReviewed(caseItem)}
                            disabled={isThisCaseRerunning || savingReviewCaseKey === caseKey(caseItem)}
                            className={`text-xs disabled:opacity-50 disabled:cursor-not-allowed ${
                              isReviewed
                                ? 'text-emerald-700 hover:text-emerald-800'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            title={isReviewed ? 'Remove reviewed mark' : 'Mark this case as reviewed'}
                          >
                            {savingReviewCaseKey === caseKey(caseItem)
                              ? 'Saving…'
                              : isReviewed
                                ? 'Unmark reviewed'
                                : 'Mark reviewed'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleCopyCaseForLlm(caseItem)}
                          disabled={exchanges.length === 0}
                          className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title='Copy this case as JSON: [{ "role": "user"|"assistant", "message": "..." }]'
                        >
                          {copiedCaseKey === caseKey(caseItem) ? 'Copied' : 'Copy for LLM'}
                        </button>
                        {typeof onEditCaseInPlan === 'function' && (
                          <button
                            type="button"
                            onClick={() => handleEditCaseInPlan(caseItem)}
                            disabled={isThisCaseRerunning}
                            className="text-xs text-violet-700 hover:text-violet-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Open this case in the test plan editor"
                          >
                            Edit in plan
                          </button>
                        )}
                        {typeof onDeleteCase === 'function' && (
                          <button
                            type="button"
                            onClick={() => handleDeleteCase(caseItem)}
                            disabled={isThisCaseRerunning}
                            className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete this test case from the current report"
                          >
                            Delete case
                          </button>
                        )}
                        {caseItem?.session_id && typeof onOpenConversation === 'function' && (
                          <button
                            type="button"
                            onClick={() => onOpenConversation(caseItem.session_id)}
                            disabled={isThisCaseRerunning}
                            className="text-xs text-[#00A884] hover:text-[#06CF9C] disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Open this conversation in the test agent"
                          >
                            Open conversation
                          </button>
                        )}
                        {typeof onRerunCases === 'function' && (
                          <>
                            <button
                              type="button"
                              onClick={() => runRerun([caseItem], { messageSource: RERUN_MESSAGE_SOURCE.REPORT })}
                              disabled={isThisCaseRerunning}
                              className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                              title="Re-run this case using the user messages shown in the report"
                            >
                              {isThisCaseRerunning ? (
                                <>
                                  <svg className="animate-spin h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                  Re-running…
                                </>
                              ) : (
                                'Re-run'
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => runRerun([caseItem], { messageSource: RERUN_MESSAGE_SOURCE.PLAN })}
                              disabled={isThisCaseRerunning}
                              className="text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Re-run this case using the latest messages from the test plan"
                            >
                              Re-run (plan)
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {caseItem?.error && (
                      <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100">
                        {caseItem.error}
                      </div>
                    )}
                    {caseItem?.note && (
                      <div className="px-3 py-2 text-xs text-blue-700 bg-blue-50 border-b border-blue-100">
                        {caseItem.note}
                      </div>
                    )}
                    {exchanges.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-500">
                        {caseStatus === 'skipped'
                          ? 'Case was skipped.'
                          : 'No exchanges recorded for this case.'}
                      </div>
                    ) : (
                      <div className="px-3 py-3 space-y-2 bg-[#F7F8FA]">
                        {exchanges.map((exchange, exIdx) => (
                          <ReportExchangeBlock
                            key={exchangeKey(caseItem, exIdx)}
                            exchange={exchange}
                            disabled={isThisCaseRerunning || anyRerunning}
                            saving={savingExchangeKey === exchangeKey(caseItem, exIdx)}
                            onUpdate={
                              typeof onMutateExchange === 'function'
                                ? (updates) =>
                                    mutateExchange(caseItem, exIdx, {
                                      action: 'update',
                                      message: updates.message,
                                      answer: updates.answer,
                                    })
                                : undefined
                            }
                            onDelete={
                              typeof onMutateExchange === 'function'
                                ? () => mutateExchange(caseItem, exIdx, { action: 'delete' })
                                : undefined
                            }
                          />
                        ))}
                        {caseStatus === 'failed' && (
                          <div className="flex justify-start">
                            <div className="max-w-[85%] rounded-2xl px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-100">
                              [No reply]
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Notes {savingNotes && <span className="text-[10px] text-gray-400">(saving…)</span>}
            </label>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder={
                persistedId
                  ? 'Add notes about this run. Notes auto-save on blur.'
                  : 'Add notes about this run. They will be saved when you click "Save report".'
              }
              rows={2}
              className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none"
              disabled={!report}
            />
          </div>
          {(actionError || actionMessage) && (
            <div
              className={`text-xs ${
                actionError ? 'text-red-700' : 'text-emerald-700'
              }`}
            >
              {actionError || actionMessage}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-gray-500">
              {persistedId ? `Report ID: ${persistedId}` : 'In-memory report (not yet saved).'}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!persistedId && (
                <button
                  type="button"
                  onClick={handleSaveReport}
                  disabled={!report || savingReport}
                  className="px-3 py-1.5 text-xs rounded bg-[#00A884] text-white hover:bg-[#06CF9C] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingReport ? 'Saving…' : 'Save report'}
                </button>
              )}
              <button
                type="button"
                onClick={handleExportJson}
                disabled={!report}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Export JSON
              </button>
              <button
                type="button"
                onClick={handleExportHtml}
                disabled={!report}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                title="Download a standalone, self-contained HTML report."
              >
                Export HTML
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={!report}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                title='Opens the print dialog. Choose "Save as PDF" as the destination to export.'
              >
                Export PDF
              </button>
              {persistedId && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deletingReport}
                  className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingReport ? 'Deleting…' : 'Delete'}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTestRunReportModal;
